import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WizardStepper, type WizardStep } from './WizardStepper';

interface WizardLayoutProps {
  title: string;
  steps: WizardStep[];
  currentStep: string;
  onStepClick: (stepKey: string) => void;
  onBack: () => void;
  onPrevStep?: () => void;
  onNextStep?: () => void;
  onComplete?: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  canProceed: boolean;
  isSubmitting?: boolean;
  /** Текст кнопки завершения (по умолчанию "Создать") */
  completeLabel?: string;
  /** Текст кнопки завершения в процессе (по умолчанию "Создание...") */
  completingLabel?: string;
  children: React.ReactNode;
}

/**
 * WizardLayout
 *
 * Обёртка для wizard-страниц:
 * - Header с кнопкой "Назад" и заголовком
 * - Stepper под header
 * - Основной контент (children)
 * - Sticky footer с навигацией: "Назад" / "Далее" / "Готово"
 */
export function WizardLayout({
  title,
  steps,
  currentStep,
  onStepClick,
  onBack,
  onPrevStep,
  onNextStep,
  onComplete,
  isFirstStep,
  isLastStep,
  canProceed,
  isSubmitting = false,
  completeLabel = 'Готово',
  completingLabel = 'Завершение...',
  children,
}: WizardLayoutProps) {
  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Назад к списку</span>
        </Button>
        <h1 className="text-xl font-semibold">{title}</h1>
      </div>

      {/* Stepper */}
      <WizardStepper steps={steps} currentStep={currentStep} onStepClick={onStepClick} />

      {/* Content */}
      <div className="flex-1 pb-24">
        {children}
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
          <Button
            variant="outline"
            onClick={onPrevStep}
            disabled={isFirstStep || isSubmitting}
          >
            Назад
          </Button>

          {isLastStep ? (
            <Button
              onClick={onComplete}
              disabled={!canProceed || isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSubmitting ? completingLabel : completeLabel}
            </Button>
          ) : (
            <Button
              onClick={onNextStep}
              disabled={!canProceed || isSubmitting}
            >
              Далее
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
